/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import {
  BLACK,
  WHITE,
  GRAY_MID,
  BLUE_FEEK,
  GREEN_LIGHT,
  PURPLE_FEEK,
  TRANSPARENT,
  PURPLE_LIGHT,
  GREEN_STATUS,
  GRAY_LIGHTEST,
} from 'styles/colors';
import APIClient from 'APIClient';
import Icon from 'components/Icon';
import Text from 'components/Text';
import {
  getFluidSize,
  getColorWithAlpha,
  getFontSizeAndLineHeigh,
} from 'utils/cssStringManipulation';
import handleError from 'utils/handleError';
import { MEDIA_QUERIES } from 'styles/constants';
import { setCampaignTypes } from 'redux/entities';
import { ROUTES_FEEKAPP } from 'constants/routes';
import FooterLanding from 'components/FooterLanding';
import { Link, useNavigate } from 'react-router-dom';
import CardCampaign from 'components/cards/CardCampaign';
import { FeekPlan } from 'screens/feekApp/Landing/FeekPlan';
import ButtonPrimary from 'components/buttons/ButtonPrimary';
import { FeekAbout } from 'screens/feekApp/Landing/FeekAbout';
import { FeekHeader } from 'screens/feekApp/Landing/FeekHeader';
import { FeekStores } from 'screens/feekApp/Landing/FeekStores';
import { FeekCategory } from 'screens/feekApp/Landing/FeekCategory';
import { FeekFeatures } from 'screens/feekApp/Landing/FeekFeatures';
import { useScrollHorizontally } from 'hooks/useScrollHorizontally';
import FeekspotsCities from 'screens/manager/Landing/FeekspotsCities';
import { ReactComponent as FeekLogo } from 'assets/icons/FeekLogo.svg';
import ModalSelectLocation from 'components/modals/ModalSelectLocation';
import { FeekExperiences } from 'screens/feekApp/Landing/FeekExperiences';
import { ReactComponent as Individual } from 'assets/icons/Individual.svg';
import ModalRequestLocation from 'screens/feekApp/Landing/ModalRequestLocation';

const styles = css`
  min-height: 100vh;

  .headerContainer {
    display: flex;
    position: absolute;
    top: 40px;
    width: calc(100% - 220px);
    align-items: center;
    padding: 18px 40px;
    margin-left: 110px;
    justify-content: space-between;
    background-color: ${getColorWithAlpha(PURPLE_FEEK, 70)};
    backdrop-filter: blur(10px) opacity(0.7);
    border-radius: 60px;
    border: 1px solid var(--ui-20-light-purple, #efeeff);
    background: #8c68cfcc;

    @media (max-width: 1024px) {
      width: calc(100% - 120px);
      margin-left: 60px;
      padding: 12px 20px;
      border-radius: 40px;
    }
  }

  .feekLogo {
    margin-right: 4px;
  }

  .title-left {
    font-size: 20px;
  }

  .borderGreenLight {
    border-width: 3px;
    font-size: 20px;
    :hover {
      opacity: 1;
      background-color: ${GREEN_LIGHT};

      * {
        color: ${BLACK};
        fill: ${BLACK};
      }
    }
  }

  .borderGreenLightLast {
    font-size: 20px;
  }

  .searchContainer {
    margin-top: ${getFluidSize(2, 3)}em;
    margin-bottom: ${getFluidSize(3, 5)}em;
    display: flex;
    flex-direction: column;
    padding-right: ${getFluidSize(10, 20)};
  }

  .location-search-container {
    flex: 1;
    position: relative;
    margin-bottom: 15px;
  }

  .placesContainer {
    margin-top: 0.5em;
    position: absolute;
    width: 100%;
    z-index: 1;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);
  }
  .buttonSearch {
    width: 100%;
    padding: 30px 40px;
    align-self: flex-start;
  }

  .scrollDownButton {
    margin-bottom: 4em;
    align-self: start;
  }

  .step-circle {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: 2px solid ${GRAY_MID};
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .upperCase {
    text-transform: uppercase;
  }

  .section {
    padding: ${getFluidSize(40, 60)} 0px ${getFluidSize(40, 60)}
      ${getFluidSize(10, 20)};
  }

  .section-title {
    font-size: 42px;
  }

  .ps-60 {
    padding-left: 60px;
  }

  .mx-150 {
    margin-left: 150px;
    margin-right: 150px;
  }
  .title {
    margin-bottom: ${getFluidSize(20, 40)};
    display: flex;
    align-items: center;
    gap: 1em;
  }

  .about-card {
    width: 100%;
    min-height: 540px;
    border-radius: 24px;
    border: 1px solid #e7e3ff;
    background: var(--ui-20-light-purple, #efeeff);
  }

  .about-card-bottom {
    width: 100%;
    min-height: ${getFluidSize(250, 350)};
    border-radius: 24px;
    border: 1px solid #e7e3ff;
    background: var(--ui-20-light-purple, #efeeff);
  }

  .main-card {
    padding: ${getFluidSize(25, 45)};
    z-index: 1;
  }

  .main-card-icon {
    display: flex;
    width: 50px;
    height: 50px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
  }

  .main-card-title {
    leading-trim: both;
    text-edge: cap;
    padding-bottom: ${getFluidSize(10, 15)};
    font-family: Roobert;
    font-style: normal;
    font-weight: 600;
    line-height: 120%; /* 33.6px */
  }

  .main-card-image {
    width: 100%;
    margin-top: ${getFluidSize(-40, -60)};
  }

  .main-card-image-2 {
    width: 112%;
    padding-left: 30px;
    margin-bottom: -100px;
    z-index: 2;
    position: relative;
  }

  .main-card-image-3 {
    width: 100%;
    margin-bottom: ${getFluidSize(-50, -100)};
  }

  .mb60 {
    margin-bottom: 60px;
  }
  .aboutButton {
    padding: 16px 30px;
  }

  .stepsContainer {
    cursor: move;
    overflow-x: scroll;
  }

  .stepsContainer::-webkit-scrollbar {
    display: none;
  }

  .stepsContainer > .stepCard {
    margin-left: 20px;
  }
  .stepCard {
    border-radius: 20px;
    border: 3px solid ${PURPLE_LIGHT};
    padding: ${getFluidSize(30, 60)};
    margin-right: ${getFluidSize(30, 70)};
    min-width: ${getFluidSize(300, 500)};
  }

  .step-title,
  .step-description {
    margin-bottom: 30px;
  }

  .mb30 {
    margin-bottom: 30px;
  }

  .ciudades {
    background-color: ${PURPLE_LIGHT};
  }

  .ciudades-content {
    overflow: hidden;
    padding-top: ${getFluidSize(80, 150)};
  }

  .ciudad-card {
    width: ${getFluidSize(300, 593)};
    padding: ${getFluidSize(20, 40)};
    border-radius: 20px;
    border: 2px solid ${WHITE};
    margin-left: auto;
    margin-right: auto;
    background-color: ${PURPLE_FEEK};
    transition: all 0.3s ease-in-out;
  }

  .ciudad-card.active {
    background-color: ${GREEN_LIGHT};
    border: 2px solid ${GREEN_LIGHT};
  }

  .badge-white {
    z-index: 10;
    transition: all 0.0025s ease-in-out;
    background-color: ${PURPLE_FEEK};
    cursor: pointer;
    border: 2px solid ${WHITE};
    width: fit-content;
    margin-top: ${getFluidSize(-10, -20)};
    margin-bottom: ${getFluidSize(100, 200)};
    margin-right: auto;
    margin-left: auto;
    color: ${BLACK};
    border-radius: 40px;
    padding: ${getFluidSize(0.8, 1.2)}em ${getFluidSize(1.2, 1.7)}em;
  }

  .badge-white.swiping {
    border: 2px solid ${PURPLE_FEEK};
  }

  .ciudad-card.active .badge-category {
    background-color: ${GREEN_STATUS};
    border: 1px solid ${GREEN_STATUS};
  }

  .ciudad-card .categories {
    margin-top: ${getFluidSize(20, 40)};
    margin-bottom: ${getFluidSize(60, 115)};
  }

  .ciudad-card .feekspots {
    margin-top: 115px;
  }

  .feekspot-image {
    width: 60px !important;
    height: 60px !important;
    border-radius: 50%;
    border: 2px solid ${WHITE};
    margin-left: -28px;
  }

  .feekspot-image:first-child {
    margin-left: 0px;
  }

  .feekspots-counter-circle {
    min-width: 60px;
    height: 60px;
    border-radius: 50%;
    border: 2px solid ${WHITE};
    margin-left: -28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ciudad-card.active .feekspots-counter-circle {
    background-color: ${WHITE};
    border: 2px solid ${GRAY_LIGHTEST};
  }

  .bottom-radius-white {
    height: 60px;
    background-color: ${WHITE};
    border-bottom-left-radius: 30px;
    border-bottom-right-radius: 30px;
  }

  .top-radius-white {
    height: 60px;
    background-color: ${WHITE};
    border-top-left-radius: 30px;
    border-top-right-radius: 30px;
  }

  .show-more-campaigns-link {
    margin-left: 70px;
    margin-bottom: 40px;
    cursor: pointer;
  }

  .campaigns-feed {
    gap: ${getFluidSize(16, 24)};
    display: flex;
    flex-direction: row;
    overflow-x: scroll;
    margin-top: 0px;
    margin-bottom: ${getFluidSize(100, 200)};
    min-height: 362px;
  }

  .campaign-card:first-of-type {
    margin-inline-start: 70px;
  }
  .campaign-card:last-of-type {
    margin-inline-end: 70px;
  }

  .campaigns-feed::-webkit-scrollbar {
    display: none;
  }

  .cities-badges {
    gap: ${getFluidSize(10, 20)};
  }
  .badge-city {
    border-radius: 30px;
    width: fit-content;
    padding: 10px 20px;
    margin-bottom: 40px;
    background-color: ${GREEN_LIGHT};
  }

  @keyframes scroll {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(calc(-100% - 40px));
    }
  }

  @keyframes scrollReverse {
    from {
      transform: translateX(calc(-100%));
    }
    to {
      transform: translateX(0 - 40px);
    }
  }

  .px-40 {
    padding-left: 3em;
    padding-right: 3em;
  }
  .px-70 {
    padding-left: 70px;
    padding-right: 70px;
  }

  .place-badge {
    border: 2px solid ${GRAY_MID};
    width: fit-content;
    border-radius: 16px;
    padding: 5px 9px;
    margin-block: 10px 20px;
  }

  .want-feek {
    display: flex;
    cursor: pointer;
    align-items: center;
    border-radius: 50px;
    margin-bottom: 150px;
    margin-right: 3.25em;
    padding: 4px 4px 4px 40px;
    border: 2px solid ${WHITE};
    justify-content: space-between;
  }

  .want-feek * {
    font-size: 16px;
  }

  .want-feek {
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    max-width: ${getFluidSize(300, 550)};
  }

  .white-circle {
    width: 60px;
    height: 60px;
    display: flex;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background-color: ${WHITE};
  }

  .bg-black {
    background-color: ${BLACK};
  }

  .ml12 {
    margin-left: 12px;
  }

  @media (min-width: 576px) {
    .want-feek * {
      font-size: 32px;
    }
    .want-feek {
      max-width: 550px;
    }

    .white-circle {
      min-width: 90px;
      min-height: 90px;
    }

    .section {
      padding: 60px 0px 60px 70px;
    }

    .h1 {
      font-size: 54px;
      margin-bottom: 80px;
      line-height: 54px;
    }

    .h2 {
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 80px;
    }

    .hero-title {
      font-size: 50px;
      line-height: 58px;
    }
  }
  ${MEDIA_QUERIES.bigScreen} {
    .stepsContainer > .stepCard {
      margin-left: 100px;
    }

    .section-title {
      font-size: 80px;
    }

    .h1 {
      font-size: 80px;
      margin-bottom: 80px;
      line-height: 80px;
    }

    .h2 {
      font-size: 48px;
      line-height: 48px;
    }

    .searchContainer {
      flex-direction: row;
    }

    .buttonSearch {
      width: fit-content;
    }

    .heroContainer {
      padding-left: 70px;
      background-position: center;
    }

    .hero-title {
      font-size: 60px;
      line-height: 68px;
    }
    .feekLogo {
      margin-right: 60px;
    }
  }

  ${MEDIA_QUERIES.tablet} {
    .ciudad-card {
      width: calc(100% - 40px);
    }

    .location-description {
      ${getFontSizeAndLineHeigh(32)}
    }

    .ciudad-card .categories {
      margin-top: 20px;
      margin-bottom: 55px;
    }

    .feekspots-counter-circle {
      margin-left: -45px;
      background-color: ${WHITE};
    }
  }
  ${MEDIA_QUERIES.mobile} {
    .what-is-info-title {
      ${getFontSizeAndLineHeigh(40)}
      margin-bottom: 40px;
    }

    .what-is-info-text {
      ${getFontSizeAndLineHeigh(16)}
      margin-bottom: 40px;
    }

    .about .title {
      padding-left: 20px;
    }
    .stepCard {
      padding: 30px;
      min-width: 335px;
      margin-right: 30px;
    }
    .step-title {
      ${getFontSizeAndLineHeigh(24)}
      margin-bottom: 30px;
    }
    .step-description {
      ${getFontSizeAndLineHeigh(14)}
      margin-bottom: 30px;
    }
    .ciudades-content {
      padding-top: 100px;
    }

    .badge-white {
      margin-bottom: 150px;
    }

    .badge-city * {
      ${getFontSizeAndLineHeigh(14)}
    }

    .location-description {
      ${getFontSizeAndLineHeigh(24)}
    }

    .campaign-card:nth-child(1) {
      margin-left: 20px;
    }
    .campaign-card-img {
      width: 330px;
    }

    .campaigns-feed {
      margin-bottom: 100px;
    }

    .ciudad-card {
      width: calc(100% - 40px);
    }

    .headerContainer {
      width: calc(100% - 40px);
      margin-left: 20px;
      top: 20px;
      border-radius: 30px;
      display: flex;
      padding: 0px 15px;
    }

    .feekLogo {
      margin-right: 8px;
      width: 10px;
    }

    .borderGreenLight {
      display: flex;
      height: 30px;
      padding: 15px 7px 15px 8px;
      align-items: center;
      gap: 10px;
    }

    .borderGreenLight svg {
      width: 10px;
      height: 12px;
    }

    .borderGreenLightLast {
      display: flex;
      height: 30px;
      padding: 17px 9px 18px 9px;
      align-items: center;
      gap: 10px;
    }

    .title-left {
      font-size: 11px;
      margin-right: 8px;
    }
  }
`;

/**
 * Landing page component for the Feek app.
 * This component renders the main landing page with various sections including header, about, features, plan, experiences, cities, campaigns, stores, and categories.
 */
function Landing() {
  const dispatch = useDispatch();

  // State for campaigns feed promotions
  const [campaignsFeedPromotions, setCampaignsFeedPromotions] =
    useState([]);
  // State for modal visibility
  const [showRequestLocationModal, setShowRequestLocationModal] =
    useState(false);
  const [showSelectLocationModal, setShowSelectLocationModal] =
    useState(false);
  // State for carousel location selection
  const [carrouselSelectedLocation, setCarrouselSelectedLocation] =
    useState(1);
  const carrouselLocationRef = useRef(1);

  const navigate = useNavigate();

  // Hook for horizontal scrolling on campaigns feed
  const campaignsFeedRef = useScrollHorizontally();

  useEffect(() => {
    const TITLE =
      'Feek | Obtén descuentos y beneficios exclusivos a cambio de una story';
    const DESCRIPTION =
      'Feek es la app para obtener descuentos y beneficios exclusivos en restaurantes, bares y experiencias a cambio de una historia de Instagram. Agenda, disfruta y gana recompensas en tu ciudad.';
    const IMAGE = `${window.location.origin}/logo512.png`;
    const URL = window.location.origin;

    document.title = TITLE;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', DESCRIPTION);
    setMeta(
      'meta[name="keywords"]',
      'content',
      'feek, beneficios exclusivos, descuentos restaurantes, recompensas, stories instagram, colaborar con marcas, feekpoints, agenda tu lugar, feekspot, experiencias, México',
    );

    setMeta('meta[property="og:title"]', 'content', TITLE);
    setMeta(
      'meta[property="og:description"]',
      'content',
      DESCRIPTION,
    );
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', URL);
    setMeta('meta[property="og:image"]', 'content', IMAGE);
    setMeta('meta[property="og:locale"]', 'content', 'es_MX');

    setMeta(
      'meta[name="twitter:card"]',
      'content',
      'summary_large_image',
    );
    setMeta('meta[name="twitter:title"]', 'content', TITLE);
    setMeta(
      'meta[name="twitter:description"]',
      'content',
      DESCRIPTION,
    );
    setMeta('meta[name="twitter:image"]', 'content', IMAGE);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', URL);
  }, []);

  useEffect(() => {
    APIClient.getCampaignsLandingPromotions(1, 10)
      .then(({ data }) => {
        setCampaignsFeedPromotions(data.docs);
      })
      .catch(handleError);

    APIClient.getCampaignTypes()
      .then(({ data }) => {
        dispatch(setCampaignTypes(data));
      })
      .catch(handleError);
  }, [dispatch]);

  useEffect(() => {
    carrouselLocationRef.current = carrouselSelectedLocation;
  }, [carrouselSelectedLocation]);

  useEffect(() => {
    const handleWindowResize = () => {
      const current = carrouselLocationRef.current;
      setCarrouselSelectedLocation(null);
      setCarrouselSelectedLocation(current);
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  return (
    <div css={styles}>
      {/* Skip to main content for accessibility */}
      <a
        href="#main-content"
        className="sr-only sr-only-focusable"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          padding: '8px',
          backgroundColor: '#000',
          color: '#fff',
          textDecoration: 'none',
        }}
      >
        Saltar al contenido principal
      </a>

      {/* Modals for location selection */}
      <ModalSelectLocation
        location={null}
        step={0}
        visible={showSelectLocationModal}
        onLocationChanged={() => {
          navigate(ROUTES_FEEKAPP.EXPLORE);
          window.scrollTo(0);
        }}
        onRequestClose={() => setShowSelectLocationModal(false)}
      />
      <ModalRequestLocation
        show={showRequestLocationModal}
        handleClose={() => setShowRequestLocationModal(false)}
      />

      {/* Header with navigation */}
      <header className="headerContainer" role="banner">
        <div className="d-flex align-items-center">
          <Icon
            icon={FeekLogo}
            color={WHITE}
            size={50}
            className="feekLogo"
            aria-label="Feek Logo"
          />
          <Text color={WHITE} className="title-left d-md-block">
            Live it, share it, <b>Feek</b> it!
          </Text>
        </div>
        <nav
          className="d-flex"
          role="navigation"
          aria-label="Main navigation"
        >
          {localStorage.getItem('currentFeekToken') ? (
            <Link to={ROUTES_FEEKAPP.PROFILE_FEEKER_EDIT}>
              <ButtonPrimary
                strokeVariant
                strokeColor={GREEN_LIGHT}
                color={TRANSPARENT}
                textColor={GREEN_LIGHT}
                pillShape
                label="Gestionar usuario"
                fontSize={20}
                className="borderGreenLight"
                icon={<Icon icon={Individual} color={GREEN_LIGHT} />}
                layoutOnly
              />
            </Link>
          ) : (
            <>
              <Link to={ROUTES_FEEKAPP.LOGIN}>
                <ButtonPrimary
                  strokeVariant
                  strokeColor={GREEN_LIGHT}
                  color={TRANSPARENT}
                  textColor={GREEN_LIGHT}
                  pillShape
                  label="Iniciar sesión"
                  fontSize={20}
                  fontSizeMobile={10}
                  className="borderGreenLight me-2 me-md-3"
                  icon={
                    <Icon icon={Individual} color={GREEN_LIGHT} />
                  }
                  layoutOnly
                />
              </Link>
              <Link
                to={ROUTES_FEEKAPP.REGISTRATION}
                className="text-decoration-none d-none d-sm-block"
              >
                <ButtonPrimary
                  strokeColor={GREEN_LIGHT}
                  strokeVariant
                  textColor={BLUE_FEEK}
                  color={GREEN_LIGHT}
                  pillShape
                  label="Registrarse"
                  className="borderGreenLightLast"
                  fontWeight={700}
                  fontSize={20}
                  fontSizeMobile={10}
                />
              </Link>
            </>
          )}
        </nav>
      </header>

      {/* Main content area */}
      <main id="main-content" role="main">
        {/* Hero section with campaign feed */}
        <FeekHeader />

        {/* About section */}
        <section aria-labelledby="about-heading">
          <FeekAbout />
        </section>

        {/* Features section */}
        <section aria-labelledby="features-heading">
          <FeekFeatures />
        </section>

        {/* Plan selection section */}
        <section aria-labelledby="plan-heading">
          <FeekPlan />
        </section>

        {/* Experiences/Rewards section */}
        <section aria-labelledby="experiences-heading">
          <FeekExperiences />
        </section>

        {/* Cities with Feekspots section */}
        <section aria-labelledby="cities-heading">
          <FeekspotsCities />
        </section>

        {/* Categories marquee section */}
        <section aria-labelledby="categories-heading">
          <FeekCategory />
        </section>

        {/* Horizontal scrollable campaigns feed */}
        <section aria-labelledby="campaigns-heading">
          <div
            className="campaigns-feed"
            ref={campaignsFeedRef}
            aria-label="Campañas disponibles"
          >
            {campaignsFeedPromotions.map((campaign, index) => (
              <CardCampaign
                className="campaign-card"
                campaignId={campaign._id}
                key={campaign._id + index || index}
                color={campaign.color}
                image={campaign.image}
                places={campaign.places}
                title={campaign.title}
                type={campaign.type._id}
                feekspot={campaign?.customer?.name}
                feekpoints={campaign?.task.feek_points}
              />
            ))}
          </div>
        </section>

        {/* Stores section */}
        <section aria-labelledby="stores-heading">
          <FeekStores />
        </section>
      </main>

      {/* Footer */}
      <FooterLanding />
    </div>
  );
}

export default Landing;
